import { useState } from "react";
import { Gift, Sparkles, Flame } from "lucide-react";
import Confetti from "react-confetti";
import { useWindowSize } from "react-use";
import { useStore } from "../context/StoreContext";
import { streakGiftAmount } from "../context/StoreContext";
import Coin from "./Coin";
import sfx from "../utils/sound";
import { toast } from "sonner";

const DailyGift = () => {
  const { canClaimDaily, claimDaily, streakDays, lastDailyClaim } = useStore();
  const [amount, setAmount] = useState(0);
  const [popping, setPopping] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const { width, height } = useWindowSize();

  // Preview matches what claimDaily will actually award:
  //   - if lastDailyClaim === yesterday → streakDays + 1
  //   - else → streak resets to 1 (100 coins)
  const yst = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  })();
  const previewStreak = canClaimDaily
    ? lastDailyClaim === yst
      ? streakDays + 1
      : 1
    : streakDays;
  const previewAmount = streakGiftAmount(Math.max(1, previewStreak));

  const handleClaim = () => {
    if (!canClaimDaily) return;
    const { amount: bonus, streakDays: newStreak } = claimDaily();
    setAmount(bonus);
    setPopping(true);
    setShowConfetti(true);
    sfx.fanfare();
    toast(
      newStreak > 1
        ? `+${bonus} coins! 🔥 ${newStreak}-day streak — keep it going!`
        : `+${bonus} coins! Come back tomorrow for more 🌟`,
      { duration: 2400 }
    );
    setTimeout(() => setPopping(false), 1600);
    setTimeout(() => setShowConfetti(false), 3500);
  };

  return (
    <div
      className="chunky-card p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-5 relative overflow-hidden text-center sm:text-left"
      style={{
        background: canClaimDaily
          ? "linear-gradient(135deg, #FFE0F5 0%, #FFF9E6 60%)"
          : "#fff",
      }}
      data-testid="daily-gift-card"
    >
      {showConfetti && (
        <Confetti
          width={width}
          height={height}
          numberOfPieces={180}
          recycle={false}
          gravity={0.25}
          colors={["#FF5A5F", "#FFB400", "#00A699", "#F368E0", "#54A0FF"]}
        />
      )}
      <div
        className="rounded-full shrink-0 flex items-center justify-center relative"
        style={{
          width: 78,
          height: 78,
          background: canClaimDaily ? "#F368E0" : "#E5E7EB",
          border: "3px solid #2b2b2b",
          boxShadow: "0 5px 0 #2b2b2b",
        }}
      >
        <Gift
          size={38}
          strokeWidth={3}
          color="white"
          className={canClaimDaily ? "wiggle" : ""}
        />
        {canClaimDaily && (
          <span
            className="absolute -top-2 -right-2 text-2xl bob"
            aria-hidden="true"
          >
            ⭐
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="inline-flex items-center gap-2 text-sm font-display flex-wrap"
          style={{ color: canClaimDaily ? "#F368E0" : "#484848" }}
        >
          <span className="inline-flex items-center gap-1">
            <Sparkles size={14} strokeWidth={3} /> Daily Gift
          </span>
          {streakDays > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs"
              style={{
                background: "#FF5A5F",
                color: "#fff",
                border: "2px solid #2b2b2b",
              }}
              data-testid="daily-streak-badge"
            >
              <Flame size={12} strokeWidth={3} /> {streakDays}-day streak
            </span>
          )}
        </div>
        <h3 className="font-display text-xl sm:text-2xl leading-tight mt-1">
          {canClaimDaily ? (
            <>Grab today's free coins!</>
          ) : (
            <>Already claimed today 🎁</>
          )}
        </h3>
        <p className="text-sm mt-1" style={{ color: "#484848" }}>
          {canClaimDaily
            ? `Tap to add ${previewAmount} shiny coins${previewStreak > 1 ? ` (streak boost!)` : ""}`
            : `Tomorrow's gift: ${streakGiftAmount(streakDays + 1)} coins — don't break the streak!`}
        </p>
      </div>
      <button
        onClick={handleClaim}
        disabled={!canClaimDaily}
        className="btn-3d btn-pink"
        data-testid="daily-claim-btn"
      >
        {canClaimDaily ? (
          <>
            <Coin size={20} /> Claim {previewAmount}
          </>
        ) : (
          <>Tomorrow</>
        )}
      </button>
      {popping && (
        <div
          className="absolute inset-0 pointer-events-none flex items-center justify-center"
          aria-hidden="true"
        >
          <div
            className="pop-in font-display text-5xl"
            style={{
              color: "#00A699",
              textShadow: "0 3px 0 #2b2b2b",
            }}
          >
            +{amount}
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyGift;
